import { Component, inject, ViewChild, effect } from '@angular/core';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { CommitteeService } from './committee.service';
import { Committee } from '@/types';
import { getCommitteeDataTableStructure } from './utils/committee-data-table-object';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { MessageService } from 'primeng/api';
import { CommitteeRepository } from '@/store/committee-state/committee-state.repository';
import { getCommitteeDeleteObject } from './utils/committee-delete-dialog-object';
import { RefParamsService } from '../ref-params/ref-params.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AuthorizationService } from '@/services/authorization.service';
import { UserRole } from '@/types/user.types';
import { createServerQuery } from '@/components/data-table-builder/server-query';

@Component({
  selector: 'sm-committee',
  imports: [
    PageHeaderComponent,
    CommonModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    DataTableBuilderComponent,
    DialogComponent,
    TranslateModule,
  ],
  templateUrl: './committee.component.html',
  styleUrl: './committee.component.scss',
})
export class CommitteeComponent {
  //services
  refParamsService = inject(RefParamsService);
  commiteeService = inject(CommitteeService);
  router = inject(Router);
  committeeRepository = inject(CommitteeRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  authService = inject(AuthorizationService);

  //breadcrumb
  breadcrumb = [
    {
      label: 'settings.committee.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.committee.breadcrumb.committee',
      routerLink: ['/settings', 'committee'],
    },
  ];

  //dialog
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  committeeDialog?: DialogObject;

  //row-actions
  rowActionsFn: (target: Committee) => DataTableRowActions = (target: Committee) => {
    const canManage = this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN]);

    const deleteAction = canManage
      ? () => {
          this.committeeDialog = getCommitteeDeleteObject({
            t: this.translate,
            store: this.committeeRepository,
            representation: target.nom,
            action: () => {
              this.deleteCommittee(target.id);
              this.dialogComponent.toggleVisible();
            },
            toggleVisiblity: () => {
              this.dialogComponent.toggleVisible();
            },
          });
          this.dialogComponent.toggleVisible();
        }
      : undefined;

    return {
      defaultActions: {
        inspect: { label: 'table.actions.inspect' },
        edit: { label: 'table.actions.edit' },
        delete: { label: 'table.actions.delete' },
      },
      inspectAction: () =>
        this.router.navigate(['/settings', 'committee', 'update', target.id], {
          queryParams: { inspect: true },
        }),
      editAction: () => this.router.navigate(['/settings', 'committee', 'update', target.id]),
      deleteAction,
    };
  };

  //data
  data: Committee[] = [];
  totalRecords = 0;

  // Server query for pagination
  serverQuery = createServerQuery({});

  //data-table-object
  dataTableObject: DynamicDataTable = getCommitteeDataTableStructure({
    store: this.committeeRepository,
    createAction: () => this.router.navigate(['/settings', 'committee', 'add']),
    rowActionsFn: this.rowActionsFn,
    serverQuery: this.serverQuery,
  });
  loading = true;

  constructor() {
    effect(() => {
      this.serverQuery.page();
      this.serverQuery.pageSize();
      this.serverQuery.search();
      this.serverQuery.sortBy();
      this.serverQuery.sortOrder();
      this.loadData();
    });
  }

  loadData() {
    this.commiteeService
      .getCommittees({
        page: this.serverQuery.page(),
        size: this.serverQuery.pageSize(),
        sort: this.serverQuery.sortBy() ? `${this.serverQuery.sortBy()},${this.serverQuery.sortOrder()}` : '',
        search: this.serverQuery.search(),
      })
      .subscribe({
        next: data => {
          this.data = data.content;
          this.totalRecords = data.totalElements;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.committee.message.error.loading'),
          });
        },
      });
  }

  deleteCommittee(id: number) {
    this.commiteeService.deleteCommittee(id).subscribe({
      next: () => {
        this.loadData();
        this.committeeRepository.reset();
        this.loading = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.committee.message.success.deleted'),
        });
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.committee.message.error.deleted'),
        });
      },
    });
  }
}
